import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePerson, useUpdatePerson } from '@/hooks/use-persons';
import { handleApiError } from '@/lib/api-client';
import { useOrganizationSettings } from '@/hooks/use-settings';

interface EditPersonDialogProps {
  personId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PersonFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state_province: string;
  country: string;
  postal_code: string;
  notes: string;
  adp_file_number: string;
  xero_employee_number: string;
  rope_access_level: string;
  rope_access_body: string;
}

const initialForm: PersonFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state_province: '',
  country: '',
  postal_code: '',
  notes: '',
  adp_file_number: '',
  xero_employee_number: '',
  rope_access_level: '',
  rope_access_body: '',
};

export function EditPersonDialog({ personId, open, onOpenChange }: EditPersonDialogProps) {
  const [form, setForm] = useState<PersonFormData>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const { data: person, isLoading } = usePerson(personId ?? 0);
  const updateMutation = useUpdatePerson();
  const { data: settings } = useOrganizationSettings();
  const crewEnabled = settings?.modules.crew ?? false;

  useEffect(() => {
    if (!person) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm({
      first_name: person.first_name || '',
      last_name: person.last_name || '',
      email: person.email || '',
      phone: person.phone || '',
      address: person.address || '',
      city: person.city || '',
      state_province: person.state_province || '',
      country: person.country || '',
      postal_code: person.postal_code || '',
      notes: person.notes || '',
      adp_file_number: person.adp_file_number || '',
      xero_employee_number: person.xero_employee_number || '',
      rope_access_level: person.rope_access_level || '',
      rope_access_body: person.rope_access_body || '',
    });
  }, [person]);

  const updateField = (field: keyof PersonFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = (val: boolean) => {
    if (!val) {
      setError(null);
    }
    onOpenChange(val);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (!personId) return;

    try {
      await updateMutation.mutateAsync({
        id: personId,
        data: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          address: form.address.trim() || undefined,
          city: form.city.trim() || undefined,
          state_province: form.state_province.trim() || undefined,
          country: form.country.trim() || undefined,
          postal_code: form.postal_code.trim() || undefined,
          notes: form.notes.trim() || undefined,
          adp_file_number: form.adp_file_number.trim(),
          xero_employee_number: form.xero_employee_number.trim(),
          ...(!crewEnabled
            ? {
                rope_access_level: form.rope_access_level as '' | 'l1' | 'l2' | 'l3',
                rope_access_body: form.rope_access_body as '' | 'irata' | 'sprat' | 'other',
              }
            : {}),
        },
      });
      toast.success('Person updated');
      handleClose(false);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Person</DialogTitle>
          <DialogDescription>Update this person's details.</DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {!isLoading && (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-person-first-name">First Name *</Label>
                  <Input
                    id="edit-person-first-name"
                    value={form.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-person-last-name">Last Name *</Label>
                  <Input
                    id="edit-person-last-name"
                    value={form.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-person-email">Email</Label>
                  <Input
                    id="edit-person-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-person-phone">Phone</Label>
                  <Input
                    id="edit-person-phone"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-person-address">Address</Label>
                <Input
                  id="edit-person-address"
                  value={form.address}
                  onChange={(e) => updateField('address', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-person-city">City</Label>
                  <Input
                    id="edit-person-city"
                    value={form.city}
                    onChange={(e) => updateField('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-person-state">State/Province</Label>
                  <Input
                    id="edit-person-state"
                    value={form.state_province}
                    onChange={(e) => updateField('state_province', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-person-country">Country</Label>
                  <Input
                    id="edit-person-country"
                    value={form.country}
                    onChange={(e) => updateField('country', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-person-postal">Postal Code</Label>
                  <Input
                    id="edit-person-postal"
                    value={form.postal_code}
                    onChange={(e) => updateField('postal_code', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-person-notes">Notes</Label>
                <Input
                  id="edit-person-notes"
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                />
              </div>

              <div className="pt-2 border-t space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Rope Access</h4>
                  {crewEnabled && (
                    <p className="text-xs text-gray-500 mt-1">
                      Derived from the person&apos;s current H3Crew certification.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select
                      value={form.rope_access_level || 'none'}
                      onValueChange={(value) =>
                        updateField('rope_access_level', value === 'none' ? '' : value)
                      }
                      disabled={crewEnabled}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not set</SelectItem>
                        <SelectItem value="l1">L1</SelectItem>
                        <SelectItem value="l2">L2</SelectItem>
                        <SelectItem value="l3">L3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Body</Label>
                    <Select
                      value={form.rope_access_body || 'none'}
                      onValueChange={(value) =>
                        updateField('rope_access_body', value === 'none' ? '' : value)
                      }
                      disabled={crewEnabled}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not set</SelectItem>
                        <SelectItem value="irata">IRATA</SelectItem>
                        <SelectItem value="sprat">SPRAT</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Payroll Integration</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Optional identifiers used only when exporting payroll to ADP or Xero.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-person-adp-file-number">ADP File Number</Label>
                    <Input
                      id="edit-person-adp-file-number"
                      value={form.adp_file_number}
                      onChange={(e) => updateField('adp_file_number', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-person-xero-employee-number">Xero Employee Number</Label>
                    <Input
                      id="edit-person-xero-employee-number"
                      value={form.xero_employee_number}
                      onChange={(e) => updateField('xero_employee_number', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
